import { useAgentDetailQuery } from 'hooks/useAgents';
import { agentRealName } from 'types/agents';
import { AgentStatusBadge } from './AgentStatusBadge';
import { Modal } from './Modal';

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-0.5 text-xl font-semibold text-gray-950">{value}</div>
    </div>
  );
}

export function AgentDetailView({ userId, onClose }: { userId: string; onClose: () => void }) {
  const { data, isLoading } = useAgentDetailQuery(userId);

  return (
    <Modal title="Agent" onClose={onClose}>
      {isLoading || !data ? (
        <div className="text-sm text-gray-500">Laddar…</div>
      ) : (
        <div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base font-medium text-gray-950">{agentRealName(data)}</div>
              <div className="mt-0.5 text-xs text-blue-600">
                alias: {data.alias} · {data.office === 'front' ? 'Front office' : 'Back office'}
              </div>
            </div>
            <AgentStatusBadge status={data.derivedStatus} />
          </div>

          <div className="mt-4 grid grid-cols-4 gap-2">
            <Stat label="Pågående" value={`${data.load.active}/${data.load.max}`} />
            <Stat label="Lösta idag" value={data.stats.resolvedToday} />
            <Stat label="Denna vecka" value={data.stats.resolvedWeek} />
            <Stat
              label="Snittsvar"
              value={data.stats.avgResponseMins == null ? '–' : `${data.stats.avgResponseMins} min`}
            />
          </div>

          <div className="mt-5 text-sm font-medium text-gray-900">
            Pågående chattar <span className="text-gray-400">{data.liveChats.length}</span>
          </div>
          {data.liveChats.length === 0 ? (
            <p className="mt-1 text-xs text-gray-400">Inga pågående chattar.</p>
          ) : (
            <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-100">
              {data.liveChats.map((chat) => (
                <li key={chat.id} className="px-3 py-2">
                  <div className="text-sm text-gray-900">{chat.customerName || 'Okänd kund'}</div>
                  {chat.lastMessage && (
                    <div className="truncate text-xs text-gray-500">{chat.lastMessage}</div>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-5 text-sm font-medium text-gray-900">Senast lösta</div>
          {data.recentResolved.length === 0 ? (
            <p className="mt-1 text-xs text-gray-400">Inga lösta ärenden ännu.</p>
          ) : (
            <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-100">
              {data.recentResolved.map((chat) => (
                <li key={chat.id} className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm text-gray-800">{chat.customerName || 'Okänd kund'}</span>
                  <span className="text-xs text-gray-400">
                    {chat.resolvedAt ? new Date(chat.resolvedAt).toLocaleDateString('sv-SE') : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
